import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const LoadingScreen = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState('init');

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('logo'), 300);
    const t2 = setTimeout(() => setPhase('tagline'), 1200);
    const t3 = setTimeout(() => setPhase('progress'), 1800);
    const t4 = setTimeout(() => setPhase('done'), 3800);
    return () => [t1, t2, t3, t4].forEach(clearTimeout);
  }, []);

  useEffect(() => {
    if (phase !== 'progress') return;
    let current = 0;
    const interval = setInterval(() => {
      current += Math.floor(Math.random() * 6) + 2;
      if (current >= 100) { current = 100; clearInterval(interval); }
      setProgress(current);
    }, 35);
    return () => clearInterval(interval);
  }, [phase]);

  useEffect(() => {
    if (phase === 'done') {
      const t = setTimeout(() => onComplete?.(), 600);
      return () => clearTimeout(t);
    }
  }, [phase, onComplete]);

  return (
    <AnimatePresence>
      {phase !== 'done' && (
        <motion.div
          key="loader"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.05 }}
          transition={{ duration: 0.6, ease: 'easeInOut' }}
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden"
          style={{ background: '#020810' }}
        >
          {/* Grid background */}
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: `linear-gradient(rgba(0,102,255,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(0,102,255,0.15) 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }} />

          {/* Center glow */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <motion.div
              animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.6, 0.3] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              style={{
                width: 500, height: 500, borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(0,102,255,0.25) 0%, rgba(0,204,255,0.1) 40%, transparent 70%)',
              }}
            />
          </div>

          {/* Electric sparks */}
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute"
              style={{ left: `${15 + i * 14}%`, top: `${20 + (i % 3) * 25}%` }}
              animate={{ opacity: [0, 1, 0], y: [0, -20, 0], scale: [0.5, 1, 0.5] }}
              transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.3, ease: 'easeInOut' }}
            >
              <svg width="12" height="20" viewBox="0 0 12 20" fill="none">
                <path d="M7 0L0 11h5L5 20l7-11H7V0z" fill="#facc15" opacity="0.8" />
              </svg>
            </motion.div>
          ))}

          {/* Main content */}
          <div className="relative flex flex-col items-center z-10">

            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.8 }}
              animate={phase !== 'init' ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="flex flex-col items-center mb-8"
            >
              <div className="relative mb-6">
                {/* Outer spinning ring */}
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
                  style={{
                    width: 120, height: 120, borderRadius: '50%',
                    border: '1px solid transparent',
                    borderTopColor: '#0066ff', borderRightColor: '#00ccff',
                    position: 'absolute', top: -10, left: -10,
                  }}
                />
                {/* Inner counter-spinning ring */}
                <motion.div
                  animate={{ rotate: -360 }}
                  transition={{ duration: 5, repeat: Infinity, ease: 'linear' }}
                  style={{
                    width: 108, height: 108, borderRadius: '50%',
                    border: '1px solid transparent',
                    borderBottomColor: '#facc15', borderLeftColor: 'rgba(250,204,21,0.4)',
                    position: 'absolute', top: -4, left: -4,
                  }}
                />
                {/* Center icon */}
                <div style={{
                  width: 100, height: 100, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #0a1628 0%, #051020 100%)',
                  border: '1px solid rgba(0,102,255,0.4)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 0 30px rgba(0,102,255,0.3), inset 0 0 20px rgba(0,102,255,0.1)',
                }}>
                  <svg width="56" height="36" viewBox="0 0 56 36" fill="none">
                    <path d="M4 2v5M8 2v5" stroke="#38bdf8" strokeWidth="1.8" strokeLinecap="round"/>
                    <rect x="2" y="7" width="8" height="5" rx="1.2" stroke="#38bdf8" strokeWidth="1.8"/>
                    <path d="M6 12v3" stroke="#38bdf8" strokeWidth="1.8" strokeLinecap="round"/>
                    <circle cx="6" cy="17" r="1.5" fill="#facc15"/>
                    <polyline points="12,18 16,18 18,10 20,26 22,14 24,22 26,18 30,18"
                      stroke="#38bdf8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    <rect x="32" y="11" width="14" height="9" rx="1.5" stroke="#38bdf8" strokeWidth="1.8"/>
                    <polygon points="46,12.5 50,10 50,22 46,19.5" fill="#38bdf8"/>
                    <circle cx="38" cy="15.5" r="2" fill="#facc15"/>
                  </svg>
                </div>
              </div>

              {/* Brand name */}
              <motion.div
                initial={{ opacity: 0, letterSpacing: '0.5em' }}
                animate={phase !== 'init' ? { opacity: 1, letterSpacing: '0.18em' } : {}}
                transition={{ duration: 1, delay: 0.3 }}
                className="text-center"
              >
                <h1 style={{
                  fontFamily: 'Georgia, "Times New Roman", serif',
                  fontSize: '2.8rem', fontWeight: 900, color: '#ffffff',
                  letterSpacing: '0.18em', lineHeight: 1, marginBottom: '6px',
                  textShadow: '0 0 40px rgba(0,102,255,0.5)',
                }}>
                  TAYYAB
                </h1>
                <p style={{
                  fontSize: '0.7rem', fontWeight: 700,
                  letterSpacing: '0.35em', color: '#38bdf8',
                  textTransform: 'uppercase', marginBottom: '4px',
                }}>
                  ELECTRIC STUDIO
                </p>
              </motion.div>
            </motion.div>

            {/* Tagline */}
            <AnimatePresence>
              {(phase === 'tagline' || phase === 'progress') && (
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  style={{
                    fontSize: '0.65rem', letterSpacing: '0.25em',
                    color: '#6b7280', textTransform: 'uppercase', marginBottom: '40px',
                  }}
                >
                  Powering Homes · Securing Futures
                </motion.p>
              )}
            </AnimatePresence>

            {/* Progress bar */}
            <AnimatePresence>
              {phase === 'progress' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                  className="flex flex-col items-center"
                  style={{ width: 260 }}
                >
                  <div style={{
                    width: '100%', height: 2,
                    background: 'rgba(255,255,255,0.07)',
                    borderRadius: 2, overflow: 'hidden', marginBottom: 10,
                  }}>
                    <div style={{
                      height: '100%',
                      background: 'linear-gradient(90deg, #0066ff, #00ccff, #facc15)',
                      borderRadius: 2, width: `${progress}%`,
                      boxShadow: '0 0 10px rgba(0,102,255,0.8)',
                      transition: 'width 0.05s linear',
                    }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <span style={{ fontSize: '0.6rem', color: '#4b5563', letterSpacing: '0.1em' }}>
                      INITIALIZING SYSTEMS
                    </span>
                    <span style={{ fontSize: '0.65rem', color: '#38bdf8', fontWeight: 700, fontFamily: 'monospace' }}>
                      {progress}%
                    </span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Corner brackets */}
          {[
            { top: 20, left: 20 },
            { top: 20, right: 20 },
            { bottom: 20, left: 20 },
            { bottom: 20, right: 20 },
          ].map((pos, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 0.4, scale: 1 }}
              transition={{ delay: 0.5 + i * 0.1, duration: 0.4 }}
              style={{
                position: 'absolute', width: 30, height: 30, ...pos,
                borderTop: i < 2 ? '1px solid rgba(0,102,255,0.6)' : 'none',
                borderBottom: i >= 2 ? '1px solid rgba(0,102,255,0.6)' : 'none',
                borderLeft: (i === 0 || i === 2) ? '1px solid rgba(0,102,255,0.6)' : 'none',
                borderRight: (i === 1 || i === 3) ? '1px solid rgba(0,102,255,0.6)' : 'none',
              }}
            />
          ))}

          {/* Bottom location */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.25 }}
            transition={{ delay: 1.5, duration: 1 }}
            style={{
              position: 'absolute', bottom: 24,
              fontSize: '0.55rem', letterSpacing: '0.2em',
              color: '#6b7280', textTransform: 'uppercase',
            }}
          >
            Fatehgarh, Baramulla · Jammu & Kashmir
          </motion.p>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingScreen;